import React from "react";
import classNames from "classnames";
import moment from "moment";
import { ReactComponent as Arrow } from "src/images/arrow-down.svg";
import TooltipPortal from "../TooltipPortal/TooltipPortal";
import {
  CriteriaOption,
  CriteriaOptionValue,
  InsightsDashboardSetup,
  InsightsDashboardSetupConfig,
  WorkspacePublicationsType,
} from "src/redux/insights-dashboard/types";
import { IDCountry } from "./panes/InsightsDashboardSetupPane/InsightsDashboardSetupPane";
import type { IDDateRange } from "./types";
import type { IDDateRangeOption } from "./InsightsDashboard";

export const breakdownOptions = [
  {
    value: "Platform",
    label: "Platform",
  },
  {
    value: "Tier",
    label: "Tier",
  },
  {
    value: "Paid vs. organic",
    label: "Paid vs. organic",
  },
  {
    value: "Country",
    label: "Country",
  },
];

export const formatIDNumber = (value: number) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
};

export const formatIDPercent = (value: number) => {
  if (value === null || value === undefined) return "-";
  return `${(value * 100).toFixed(2)}%`;
};

export const renderComparisonValue = (
  value?: number | null,
  tooltipMessage?: string | null,
) => {
  if (value === null || value === undefined) return null;
  return (
    <TooltipPortal
      content={`In ${tooltipMessage?.toLowerCase()}`}
      contentHidden={!tooltipMessage || tooltipMessage === "custom"}
    >
      <div
        className={classNames("comparison-value", {
          "comparison-value--positive": value > 0,
          "comparison-value--negative": value < 0,
        })}
      >
        <Arrow />
        {formatIDPercent(Math.abs(value))}
      </div>
    </TooltipPortal>
  );
};

export const getFiltersFromSetup = (
  setup: InsightsDashboardSetup,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setupConfig: InsightsDashboardSetupConfig | null,
) => {
  const { hashtags, countries, mentions, platforms } = setup;

  const filters = {
    countries: countries ?? [],
    platforms: platforms ?? [],
    hashtags: hashtags,
    mentions: mentions,
  };

  return filters;
};

// === Scalar <-> rich-object mapping for persisted dashboard filters ===
//
// The UI keeps rich objects (countries as {value,label,locale}, criteria as
// {value,label}) so components like SelectTags/CriteriaOptionSelector can render
// labels. Persisted filters only ever store scalars (country codes, "or"/"and"
// strings). "toRequest" mappers dehydrate rich UI state right before it's sent to
// the backend; "fromStored" mappers hydrate scalars back into rich UI state right
// after a dashboard is loaded. Dashboards saved before this change stored rich
// objects directly; they are not supported here and are expected to fail until
// backfilled by a separate migration.

const countryToCode = (country: IDCountry): string => country.value;

const codeToCountry = (
  code: string,
  lookup: IDCountry[],
  labels?: Record<string, string>,
): IDCountry =>
  lookup.find((country) => country.value === code) || {
    value: code,
    label: labels?.[code] || code,
    locale: "",
  };

const criteriaToValue = (criteria: CriteriaOption): CriteriaOptionValue =>
  criteria.value;

const valueToCriteria = (value: CriteriaOptionValue): CriteriaOption => ({
  value,
});

const getCountriesLookup = (
  setupConfig: InsightsDashboardSetupConfig | null,
  workspacePublicationsType: InsightsDashboardSetup["workspacePublicationsType"],
): IDCountry[] =>
  (workspacePublicationsType === WorkspacePublicationsType.WorkspaceOnly
    ? setupConfig?.wsCountries
    : setupConfig?.countries) || [];

export interface InsightsDashboardSetupRequest
  extends Omit<
    InsightsDashboardSetup,
    | "countries"
    | "hashtagsCriteria"
    | "mentionsCriteria"
    | "hashtagsMentionsCriteria"
  > {
  countries: string[];
  hashtagsCriteria: CriteriaOptionValue;
  mentionsCriteria: CriteriaOptionValue;
  hashtagsMentionsCriteria: CriteriaOptionValue;
}

export const mapInsightsDashboardSetupToRequest = (
  setup: InsightsDashboardSetup,
): InsightsDashboardSetupRequest => ({
  ...setup,
  countries: (setup.countries || []).map(countryToCode),
  hashtagsCriteria: criteriaToValue(setup.hashtagsCriteria),
  mentionsCriteria: criteriaToValue(setup.mentionsCriteria),
  hashtagsMentionsCriteria: criteriaToValue(setup.hashtagsMentionsCriteria),
});

export const mapInsightsDashboardDateRangeToRequest = (
  dateRange: IDDateRangeOption | null,
): string | null => (dateRange ? dateRange.value : null);

// dateFrom/dateTo are only meaningful for a "custom" date range — for any preset
// range the backend recomputes a fresh window from "now" on every request and
// ignores the saved values. Persisting a saved preset's stale dateFrom/dateTo
// serves no purpose, so null them out here before they're sent to the backend.
export const mapInsightsDashboardDatesToRequest = (
  dateRange: IDDateRangeOption | null,
  dateFrom: string | null,
  dateTo: string | null,
): { dateFrom: string | null; dateTo: string | null } =>
  dateRange?.value === "custom"
    ? { dateFrom, dateTo }
    : { dateFrom: null, dateTo: null };

export const hydrateInsightsDashboardSetup = (
  setup: InsightsDashboardSetupRequest,
  setupConfig: InsightsDashboardSetupConfig | null,
): InsightsDashboardSetup => {
  const lookup = getCountriesLookup(
    setupConfig,
    setup.workspacePublicationsType,
  );

  return {
    ...setup,
    countries: (setup.countries || []).map((code) =>
      codeToCountry(code, lookup),
    ),
    hashtagsCriteria: valueToCriteria(setup.hashtagsCriteria),
    mentionsCriteria: valueToCriteria(setup.mentionsCriteria),
    hashtagsMentionsCriteria: valueToCriteria(setup.hashtagsMentionsCriteria),
  };
};

const findDateRangeOption = (
  value: string,
  dateRanges: IDDateRange[],
): { value: string; label: string } | null => {
  for (const section of dateRanges || []) {
    if (section.type === "custom") {
      if (section.value === value) return section;
      continue;
    }

    if (section.type === "monthly") {
      for (const yearGroup of section.years) {
        const option = yearGroup.options.find((o) => o.value === value);
        if (option) return option;
      }
      continue;
    }

    const option = section.options.find((o) => o.value === value);
    if (option) return option;
  }

  return null;
};

export const hydrateInsightsDashboardDateRange = (
  dateRange: string | null,
  dateRanges: IDDateRange[],
): IDDateRangeOption | null => {
  if (!dateRange) return null;

  const found = findDateRangeOption(dateRange, dateRanges);

  return found
    ? { value: found.value, label: found.label }
    : { value: dateRange, label: dateRange };
};

export const getInsightsDashboardDefaultDates = () => {
  const dateTo = moment().format("YYYY-MM-DD");
  const dateFrom = moment().subtract(89, "days").format("YYYY-MM-DD");

  return {
    dateRange: {
      value: "last.90days",
      label: "Last 90 days",
    },
    dateFrom,
    dateTo,
  };
};
